export type TeamRegion = "us" | "eu";

export function normalizeTeamRegion(raw: unknown): TeamRegion {
  return String(raw || "").trim().toLowerCase() === "eu" ? "eu" : "us";
}

export function isEuTeamRegion(raw: unknown): boolean {
  return normalizeTeamRegion(raw) === "eu";
}

/** True on UK/EU regional fan hosts (e.g. astonvilla.uk.mymomento.com). */
export function isUkRegionalFanHost(hostname?: string): boolean {
  const host = String(
    hostname
      || (typeof window !== "undefined" ? window.location.hostname : ""),
  ).toLowerCase();
  if (!host) return false;
  if (host.includes(".uk.")) return true;
  return host.split(".").filter(Boolean).includes("uk");
}

/**
 * Date order follows the team's US/EU setting. If that is unset, UK/EU hosts
 * default to EU (DD/MM/YYYY) instead of US.
 */
export function resolveFanTeamRegion(
  raw: unknown,
  hostname?: string,
): TeamRegion {
  const trimmed = String(raw || "").trim().toLowerCase();
  if (trimmed === "eu") return "eu";
  if (trimmed === "us") return "us";
  return isUkRegionalFanHost(hostname) ? "eu" : "us";
}

export function dobPlaceholder(region: TeamRegion): string {
  return region === "eu" ? "DD/MM/YYYY" : "MM/DD/YYYY";
}

export function maskSlashDateInput(value: string): string {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function parseDisplayDateParts(
  value: string,
  region: TeamRegion,
): { month: number; day: number; year: number } | null {
  const match = String(value || "").trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const first = parseInt(match[1], 10);
  const second = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  const month = region === "eu" ? second : first;
  const day = region === "eu" ? first : second;
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
    return null;
  }
  const d = new Date(year, month - 1, day);
  if (
    d.getMonth() !== month - 1
    || d.getDate() !== day
    || d.getFullYear() !== year
  ) {
    return null;
  }
  return { month, day, year };
}

/** Persist DOB as MM/DD/YYYY regardless of on-screen order. */
export function displayDobToCanonical(
  value: string,
  region: TeamRegion,
): string {
  const parts = parseDisplayDateParts(value, region);
  if (!parts) return String(value || "").trim();
  return `${pad2(parts.month)}/${pad2(parts.day)}/${parts.year}`;
}

export function canonicalDobToDisplay(
  value: string,
  region: TeamRegion,
): string {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed === "1") return "";
  const parts = parseDisplayDateParts(trimmed, "us");
  if (!parts) return trimmed;
  if (region === "eu") {
    return `${pad2(parts.day)}/${pad2(parts.month)}/${parts.year}`;
  }
  return `${pad2(parts.month)}/${pad2(parts.day)}/${parts.year}`;
}

export function numericDateFnsFormat(region: TeamRegion): string {
  return region === "eu" ? "dd/MM/yyyy" : "MM/dd/yyyy";
}

export function shortMonthDateFnsFormat(region: TeamRegion): string {
  return region === "eu" ? "dd/MM/yyyy" : "MMM d, yyyy";
}

export function todayShortDateFnsFormat(region: TeamRegion): string {
  return region === "eu" ? "dd/MM/yyyy" : "MMM d";
}

export function longWeekdayDateFnsFormat(region: TeamRegion): string {
  return region === "eu" ? "EEEE, dd/MM/yyyy" : "EEEE, MMM d, yyyy";
}

/** Operator game date+time. US keeps historical MM-dd-yyyy. */
export function operatorGameDateTimeFnsFormat(region: TeamRegion): string {
  return region === "eu" ? "dd/MM/yyyy hh:mm a" : "MM-dd-yyyy hh:mm a";
}
